import { CropFieldAdvice } from '@next/arable-lib/advice';
import { CropField } from '@next/arable-lib/crop-field';
import { createFeature, createReducer, on } from '@ngrx/store';

import {
  loadAutomaticAdviceSuccess,
  loadCompanySettingsSuccess,
  loadCropFieldAdvicesSuccess,
  loadCropVarietiesByCropIdSuccess,
  loadCustomerOrderPlan,
  loadCustomerOrderPlanSuccess,
  loadExistingCropFieldsSuccess,
  loadFertilizerOrderLines,
  loadFertilizerOrderLinesSuccess,
  loadFertilizerOrderPlan,
  loadFertilizerOrderPlanSuccess,
  loadGBMOrderLines,
  loadGBMOrderLinesSuccess,
  loadHasAnyFinalizedOrderLinesSuccess,
  loadOrderLines,
  loadOrderLinesSuccess,
  loadSalesOrderPeriodsSuccess,
  setCropAidPackages,
  setCropAids,
  setPackages,
} from './actions';
import { cropYearChanged } from '../../store/actions';
import {
  CropAid,
  CropAidPackage,
  CropVarietiesByCropId,
  CustomerOrderPlan,
  FertilizerOrderPlanResponse,
  GetAutomaticAdviceResponse,
  GetCompanySettingsResponse,
  OrderLine,
  Package,
  SalesOrderPeriod,
} from '../../shared/models';

export class ManualCropStore {
  cropVarieties: CropVarietiesByCropId[];
  customerOrderPlan: CustomerOrderPlan[] | null;
  orderLines: OrderLine[] | null; // Legacy field for backward compatibility
  gbmOrderLines: OrderLine[] | null;
  fertilizerOrderLines: OrderLine[] | null;
  fertilizerOrderPlan: FertilizerOrderPlanResponse | null;
  cropAids: CropAid[];
  cropAidPackages: CropAidPackage[];
  packages: Package[];
  salesOrderPeriods: SalesOrderPeriod[];

  constructor() {
    this.cropVarieties = [];
    this.customerOrderPlan = [];
    this.orderLines = [];
    this.gbmOrderLines = [];
    this.fertilizerOrderLines = [];
    this.fertilizerOrderPlan = null;
    this.cropAids = [];
    this.cropAidPackages = [];
    this.packages = [];
    this.salesOrderPeriods = [];
  }
}

export interface AdviceState {
  manualCrops: ManualCropStore;
  hasAnyFinalizedOrderLines: boolean;
  automaticAdvice: GetAutomaticAdviceResponse | undefined;
  companySettings: GetCompanySettingsResponse | undefined;
  cropFieldAdvices: CropFieldAdvice[];
  existingCropFields: CropField[];
}

export const initialState: AdviceState = {
  manualCrops: new ManualCropStore(),
  hasAnyFinalizedOrderLines: false,
  automaticAdvice: undefined,
  companySettings: undefined,
  cropFieldAdvices: [],
  existingCropFields: [],
};

export const advicesFeature = createFeature({
  name: 'advices',
  reducer: createReducer(
    initialState,

    on(
      loadCompanySettingsSuccess,
      (state, { payload: { companySettings } }): AdviceState => ({
        ...state,
        companySettings,
      }),
    ),

    on(
      loadAutomaticAdviceSuccess,
      (state, { payload: { automaticAdvice } }): AdviceState => ({
        ...state,
        automaticAdvice,
      }),
    ),

    on(
      loadCropFieldAdvicesSuccess,
      (state, { payload: { cropFieldAdvices } }): AdviceState => ({
        ...state,
        cropFieldAdvices,
      }),
    ),

    on(
      loadExistingCropFieldsSuccess,
      (state, { payload: { existingCropFields } }): AdviceState => ({
        ...state,
        existingCropFields,
      }),
    ),

    on(cropYearChanged, (state): AdviceState => ({
      ...state,
      manualCrops: {
        ...state.manualCrops,
        customerOrderPlan: null,
        orderLines: null,
        gbmOrderLines: null,
        fertilizerOrderLines: null,
        fertilizerOrderPlan: null,
        cropAidPackages: [],
        salesOrderPeriods: [],
      },
      hasAnyFinalizedOrderLines: false,
      automaticAdvice: undefined,
      companySettings: undefined,
      cropFieldAdvices: [],
      existingCropFields: [],
    })),

    on(loadCropVarietiesByCropIdSuccess, (state, { cropVarieties }): AdviceState => {
      const existingVarieties = state.manualCrops?.cropVarieties || [];

      if (!cropVarieties || cropVarieties.length === 0) {
        return state;
      }

      const cropVarietiesList = cropVarieties.reduce((acc, cv) => {
        const existingIndex = acc.findIndex((existing) => existing.cropId === cv.cropId);
        if (existingIndex >= 0) {
          // Replace existing item immutably
          return [...acc.slice(0, existingIndex), cv, ...acc.slice(existingIndex + 1)];
        } else {
          // Add new item immutably
          return [...acc, cv];
        }
      }, [...existingVarieties]);

      return {
        ...state,
        manualCrops: { ...state.manualCrops, cropVarieties: cropVarietiesList },
      };
    }),

    on(
      loadHasAnyFinalizedOrderLinesSuccess,
      (state, { payload: { hasAnyFinalizedOrderLines } }): AdviceState => ({
        ...state,
        hasAnyFinalizedOrderLines,
      }),
    ),

    on(
      loadCustomerOrderPlanSuccess,
      (state, { payload: { customerOrderPlan } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          customerOrderPlan,
        },
      }),
    ),

    on(
      loadCustomerOrderPlan,
      (state): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          customerOrderPlan: null,
        },
      }),
    ),

    // Legacy order lines actions for backward compatibility
    on(
      loadOrderLines,
      (state): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          orderLines: null,
        },
      }),
    ),

    on(
      loadOrderLinesSuccess,
      (state, { payload: { orderLines } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          orderLines,
        },
      }),
    ),

    // GBM order lines
    on(
      loadGBMOrderLines,
      (state): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          gbmOrderLines: null,
        },
      }),
    ),

    on(
      loadGBMOrderLinesSuccess,
      (state, { payload: { orderLines } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          gbmOrderLines: orderLines,
          orderLines, // Also update legacy field for backward compatibility
        },
      }),
    ),

    // Fertilizer order lines
    on(
      loadFertilizerOrderLines,
      (state): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          fertilizerOrderLines: null,
        },
      }),
    ),

    on(
      loadFertilizerOrderLinesSuccess,
      (state, { payload: { orderLines } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          fertilizerOrderLines: orderLines,
        },
      }),
    ),

    // Fertilizer order plan
    on(
      loadFertilizerOrderPlan,
      (state): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          fertilizerOrderPlan: null,
        },
      }),
    ),

    on(
      loadFertilizerOrderPlanSuccess,
      (state, { payload: { orderPlan } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          fertilizerOrderPlan: orderPlan,
        },
      }),
    ),

    on(
      setCropAids,
      (state, { cropAids }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          cropAids,
        },
      }),
    ),

    on(
      setCropAidPackages,
      (state, { cropAidPackages }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          cropAidPackages,
        },
      }),
    ),

    on(
      setPackages,
      (state, { packages }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          packages,
        },
      }),
    ),

    on(
      loadSalesOrderPeriodsSuccess,
      (state, { payload: { salesOrderPeriods } }): AdviceState => ({
        ...state,
        manualCrops: {
          ...state.manualCrops,
          salesOrderPeriods,
        },
      }),
    ),
  ),
});

export const { selectAdvicesState } = advicesFeature;
